import { Component } from '@theme/component';

/**
 * PDP gallery (snippets/pdp-gallery.liquid): thumbnail buttons swap the photo
 * in the main tile. Every photo is already rendered in the main tile as a
 * slide, all but the active one `hidden`; a click shows the matching slide,
 * moves `aria-current` to the clicked thumbnail and announces the switch
 * through the status line (its text comes from the thumbnail's
 * `data-status`, rendered in Liquid).
 *
 * A gallery with one photo renders no thumbnails and no status line; the
 * component then has nothing to do.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides - Photos in the main tile, in gallery order.
 * @property {HTMLButtonElement[]} [thumbs] - Thumbnail buttons, same order as the slides.
 * @property {HTMLElement} [status] - Visually hidden live region.
 *
 * @extends {Component<Refs>}
 */
class PdpGalleryComponent extends Component {
  requiredRefs = ['slides'];

  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    super.connectedCallback();

    const { slides, thumbs, status } = this.refs;
    if (!thumbs?.length) return;
    if (thumbs.length !== slides.length) {
      throw new Error('pdp-gallery-component: thumbnails and slides differ in count');
    }
    if (!status) throw new Error('pdp-gallery-component: status line is missing');

    this.#listeners = new AbortController();
    this.addEventListener('click', this.#showClickedPhoto, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #showClickedPhoto = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('.pdp-gallery__thumb');
    if (!(thumb instanceof HTMLButtonElement)) return;

    const index = (this.refs.thumbs ?? []).indexOf(thumb);
    if (index === -1) return;
    this.#show(index);
  };

  /**
   * @param {number} index
   */
  #show(index) {
    const { slides, thumbs = [], status } = this.refs;

    for (const [slideIndex, slide] of slides.entries()) {
      slide.hidden = slideIndex !== index;
    }
    for (const [thumbIndex, thumb] of thumbs.entries()) {
      thumb.setAttribute('aria-current', String(thumbIndex === index));
    }

    if (status) status.textContent = thumbs[index]?.dataset.status ?? '';
  }
}

if (!customElements.get('pdp-gallery-component')) {
  customElements.define('pdp-gallery-component', PdpGalleryComponent);
}
